import { useParams, Link, useNavigate } from 'react-router';
import { useAdminApiQuery } from '@/hooks/use-admin-api-query';
import { qk } from '@/lib/query/keys';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { ChannelDetailCard } from '@/components/super-admin/channel-detail-card';
import { DetailLoadError } from '@/components/super-admin/detail-load-error';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { PageContainer } from '@/components/page-container';

interface Channel {
  id: string;
  type: 'whatsapp' | 'instagram' | 'facebook';
  displayName: string;
  metaWabaId: string;
  metaBusinessId: string;
  whatsappWabaName: string;
  whatsappBusinessName: string;
  whatsappPhoneNumberId: string;
  whatsappDisplayPhoneNumber: string;
  maskedToken: string;
  tokenStatus: string;
  tokenExpiresAt: string | null;
  metaConnected: boolean;
  forwardingEnabled: boolean;
  createdAt: string;
  lastRefreshedAt: string | null;
  consecutiveFailures: number;
}

interface Subscription {
  id: string;
  planSlug: string;
  status: string;
  stripeSubscriptionId: string | null;
  currentPeriodEnd: string | null;
}

interface ChannelDetailResponse {
  channel: Channel;
  workspace: { id: string; name: string };
  siblingChannels: Channel[];
  subscription: Subscription | null;
}

export function SuperAdminChannelDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data = null, isLoading, isError, error, refetch } = useAdminApiQuery<ChannelDetailResponse>(
    qk.superAdmin('channels', id),
    `/super-admin/channels/${id}`,
  );

  if (isError) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Link to="/super-admin/channels" className="hover:text-foreground">
            Channels
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span>...</span>
        </div>
        <DetailLoadError
          error={error}
          entity="Channel"
          backTo="/super-admin/channels"
          backLabel="Back To Channels"
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Link to="/super-admin/channels" className="hover:text-foreground">
            Channels
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Skeleton className="h-4 w-24" />
          <ChevronRight className="h-3 w-3" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-7 w-48" />
        <Card>
          <CardContent className="space-y-3 py-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  return (
    <PageContainer variant="fluid" className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2"
        onClick={() => navigate(-1)}
      >
        <ChevronLeft className="h-4 w-4" />
        Back
      </Button>

      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link to="/super-admin" className="hover:text-foreground">
          Workspaces
        </Link>
        <ChevronRight className="h-3 w-3" />
        <Link
          to={`/super-admin/workspaces/${data.workspace.id}`}
          className="hover:text-foreground"
        >
          {data.workspace.name}
        </Link>
        <ChevronRight className="h-3 w-3" />
        <span>{data.channel.displayName}</span>
      </div>

      <h1 className="text-xl font-semibold">{data.channel.displayName}</h1>

      <ChannelDetailCard channel={data.channel} />
    </PageContainer>
  );
}
